import React, { useState, useEffect, useCallback } from "react";
import { Modal } from "../../molecules/Modal";
import { LoadingOverlay, BackButton } from "../../molecules";
import { useAppDispatch, useAppSelector } from "hooks";
import { setLayout, setAIMCATChatStatus, setToggleMobileMenu, ai_mcat_chat_status } from "state/reducers/layoutSlice";
import { setMcatChatAuthInfo, setMcatChatSelectedSubject, setMcatChatInitialContext, setMcatChatExamInfo, setMcatChatCancelPendingTransition, setShowMcatChatErrorModal } from "state/reducers/AuthSlice";
import { useAuthenticateUserMutation } from "services/mcatChat/mcatChatExternal";
import { useStartConversationMutation } from "services/mcatChat/mcatChatConversation";
import { MCatChatStartConversationResponse } from "services/mcatChat/types";
import { decodeToken } from "utils/functions/decodeToken";
import { addShowAITriggerToScoreReportUrl as addAIEnalbedToUrl } from "utils/functions/addShowAITriggerToScoreReportUrl";
import "./index.scss";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  theme?: "light" | "dark";
  title?: string;
  aiChatDrawerStatus?: ai_mcat_chat_status;
  showCopyLink?: boolean; // controlled by parent, defaults hidden
}

export const IframeModalView = ({
  isOpen,
  onClose,
  url,
  theme = "light",
  title = "Content",
  aiChatDrawerStatus = "hidden",
  showCopyLink = false
}: IProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showCopied, setShowCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [previousAiState, setPreviousAiState] = useState<ai_mcat_chat_status | null>(null);
  const [copyLinkVisible, setCopyLinkVisible] = useState(showCopyLink);
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const hasHandledCloseRef = React.useRef(false);

  // Determine if AI is enabled for the user (match openTestOrDrill logic)
  const isAiEnabled = () => {
    const token = decodeToken();
    return (
      !!token &&
      typeof token.showaitutor === "string" &&
      token.showaitutor.toLowerCase() === "true"
    );
  };


  // Function to remove IsAiEnabled query parameter while preserving others
  const removeShowAITriggerParam = (urlToModify: string): string => {
    if (!urlToModify) return urlToModify;
    
    try {
      const urlObj = new URL(urlToModify);
      urlObj.searchParams.delete('IsAiEnabled');
      return urlObj.toString();
    } catch (e) {
      // If URL parsing fails, try simple string manipulation
      // Remove ?IsAiEnabled=true or &IsAiEnabled=true
      return urlToModify
        .replace(/[?&]IsAiEnabled=true(?=&|$)/, '')
        .replace(/IsAiEnabled=true&/, '')
        .replace(/[?&]IsAiEnabled(?=&|$)/, '')
        .replace(/IsAiEnabled&/, '');
    }
  };

  // Get the URL to display in iframe (add IsAiEnabled=true when needed)
  const getIframeUrl = (): string => {
    if (!url) return url;
    if (isAiEnabled()) {
      return addAIEnalbedToUrl(url);
    }
    return url;
  };

  // Get the URL to copy (strip IsAiEnabled to avoid sharing AI flag)
  const getUrlToCopy = (): string => {
    // Use the URL from the message if available, otherwise fall back to prop URL
    const urlToUse = currentUrl || url;
    if (!urlToUse) return urlToUse;
    if (isAiEnabled()) {
      return removeShowAITriggerParam(urlToUse);
    }
    return urlToUse;
  };
  
  const dispatch = useAppDispatch();
  const { aiMCATChatDrawerStatus } = useAppSelector((state) => state.layout);
  const { mcatChatExamInfo, mcatChatInitialContext, mcatChatSelectedSubject } = useAppSelector((state) => state.auth);
  const [authenticateUser, { isSuccess: isAuthSuccess }] = useAuthenticateUserMutation();
  const [startConversation] = useStartConversationMutation();

  // Reset loading state when URL changes - MOVED BEFORE EARLY RETURN
  React.useEffect(() => {
    if (url) {
      setIsLoading(true);
    }
  }, [url]);

  // Reset fullscreen state when modal is closed
  React.useEffect(() => {
    if (!isOpen) {
      // Prevent re-running this logic if we've already handled the close
      // This prevents a race condition where clearing Redux states triggers this effect again
      if (hasHandledCloseRef.current) {
        return;
      }
      
      setIsFullscreen(false);
      setPreviousAiState(null);
      hasHandledCloseRef.current = true;
      // Check if there's a pending transition (transition warning is showing)
      // This is indicated by mcatChatInitialContext or mcatChatSelectedSubject being set
      const hasPendingTransition = mcatChatInitialContext || mcatChatSelectedSubject;
      
      if (hasPendingTransition) {
        // If transition warning is showing, treat iframe close as "Cancel"
        // Signal to AiMcatChat component to cancel the pending transition
        dispatch(setMcatChatCancelPendingTransition(true));
        // Don't close the AI panel - AiMcatChat will handle the cancel logic
        return;
      }

    } else {
      // Reset the flag when modal opens
      hasHandledCloseRef.current = false;
    }
  }, [isOpen, aiMCATChatDrawerStatus, mcatChatExamInfo, mcatChatInitialContext, mcatChatSelectedSubject, dispatch]);

  // Reset fullscreen state when URL changes (different report opened)
  React.useEffect(() => {
    setIsFullscreen(false);
    setPreviousAiState(null);
    setCurrentUrl(null); // Reset current URL when prop URL changes
  }, [url]);

  // Handle authentication success to expand sidepanel
  useEffect(() => {
    if (isAuthSuccess) {
      dispatch(setLayout('toggle-desktop-menu'));
      dispatch(setAIMCATChatStatus('open'));
      dispatch(setToggleMobileMenu(false));
    }
  }, [isAuthSuccess, dispatch]);

  // Keep internal copy-link visibility in sync with controlled prop
  useEffect(() => {
    setCopyLinkVisible(showCopyLink);
  }, [showCopyLink]);

  // Hide copy link button and reset URL when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCopyLinkVisible(false);
      setCurrentUrl(null);
    }
  }, [isOpen]);



  // Listen for messages from the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Let MessageBridge handle transport, IframeModalView handles business logic
      let messageData = event.data;

      if (event.data?.type === "SCORE_REPORT_LOADED") {
        // Store the URL from the message if provided
        if (event.data?.url) {
          setCurrentUrl(event.data.url);
        }
        setCopyLinkVisible(true);
        return;
      }

      // Handle MessageBridge format - data is now directly in the message
      if (event.data && event.data.type === "AI_TRIGGER_CLICKED") {
        // MessageBridge now sends data directly in the message object
        messageData = event.data;
        
        // Check if we have the required fields
        if (!messageData.initialContext) {
          return;
        }
        
        // If iframe is in fullscreen mode, exit fullscreen when AI tutor is triggered
        if (isFullscreen) {
          // Exit fullscreen - restore previous AI state if it was open
          if (previousAiState && previousAiState !== 'hidden') {
            dispatch(setAIMCATChatStatus(previousAiState));
            // Also restore the layout if AI was open
            if (previousAiState === 'open') {
              dispatch(setLayout('toggle-desktop-menu'));
            }
          }
          setPreviousAiState(null);
          setIsFullscreen(false);
        }
      } else {
        // Not a MessageBridge AI trigger message, ignore
        return;
      }

      // Check if message contains initialContext
      if (messageData && messageData.initialContext) {
        
        // Extract subject from MessageBridge data
        let scrapedSubject = null;
        
        // MessageBridge provides subject directly
        if (messageData.subject) {
          scrapedSubject = messageData.subject;
        }
        // Fallback to structuredQuestionData
        else if (messageData.structuredQuestionData?.subject) {
          scrapedSubject = messageData.structuredQuestionData.subject;
        }
        
        // Extract exam information from initialContext JSON string
        let examName = '';
        let section = '';
        let questionNumber = '';
        
        if (messageData.initialContext && typeof messageData.initialContext === 'string') {
          try {
            const contextData = JSON.parse(messageData.initialContext);
            examName = contextData['exam-name'] || contextData.examName || '';
            section = contextData.section || '';
            questionNumber = contextData['question-number'] || contextData.questionNumber || '';
          } catch (error) {
            // Failed to parse initialContext JSON
          }
        }
        
        // Also try to get exam info from structuredQuestionData as backup
        if (!examName && !section && !questionNumber && messageData.structuredQuestionData) {
          examName = messageData.structuredQuestionData['exam-name'] || messageData.structuredQuestionData.examName || '';
          section = messageData.structuredQuestionData.section || '';
          questionNumber = messageData.structuredQuestionData['question-number'] || messageData.structuredQuestionData.questionNumber || '';
        }
        
        // Store the initialContext, subject, and exam info for later use when user clicks "Start New Session"
        dispatch(setMcatChatInitialContext(messageData.initialContext));
        dispatch(setMcatChatSelectedSubject(scrapedSubject || 'General'));
        
        // Store exam info in Redux
        const examInfo = {
          examName,
          section,
          questionNumber
        };
        dispatch(setMcatChatExamInfo(examInfo));
      }
    };

    // Always add message listener - MessageBridge can send messages anytime
    window.addEventListener('message', handleMessage);

    // Clean up listener when component unmounts
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [dispatch, isFullscreen, previousAiState, aiMCATChatDrawerStatus, mcatChatExamInfo]);


  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleCopyUrl = async () => {
    const urlToCopy = getUrlToCopy();
    try {
      await navigator.clipboard.writeText(urlToCopy);
      setShowCopied(true);

      // Hide the "Copied!" message after 2 seconds
      setTimeout(() => {
        setShowCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = urlToCopy;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);

      setShowCopied(true);
      setTimeout(() => {
        setShowCopied(false);
      }, 2000);
    }
  };

  const handleCopyKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCopyUrl();
    }
  };

  const handleFullscreenToggle = () => {
    if (!isFullscreen) {
      // Entering fullscreen - save current AI state and collapse it if visible
      // Use 'closed' instead of 'hidden' to preserve the session state
      if (aiMCATChatDrawerStatus !== 'hidden') {
        setPreviousAiState(aiMCATChatDrawerStatus);
        dispatch(setAIMCATChatStatus('closed'));
        dispatch(setLayout(null));
      } else {
        setPreviousAiState(null);
      }
    } else {
      // Exiting fullscreen - restore previous AI state if it was open
      if (previousAiState && previousAiState !== 'hidden') {
        dispatch(setAIMCATChatStatus(previousAiState));
        // Also restore the layout if AI was open
        if (previousAiState === 'open') {
          dispatch(setLayout('toggle-desktop-menu'));
        }
      }
      setPreviousAiState(null);
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleFullscreenKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleFullscreenToggle();
    }
  };

  // Early return AFTER all hooks
  if (!isOpen) return null;

  // Determine the modal style based on AI chat drawer status
  const getModalStyle = () => {
    if (aiChatDrawerStatus === 'open') {
      return "iframe-modal-style iframe-modal-ai-open";
    } else if (aiChatDrawerStatus === 'closed') {
      return "iframe-modal-style iframe-modal-ai-collapsed";
    }
    return "iframe-modal-style";
  };

  const modalStyle = getModalStyle();

  const wrapperClass = isFullscreen
    ? "iframe-modal-wrapper iframe-modal-fullscreen"
    : "iframe-modal-wrapper";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      theme={theme}
      style={modalStyle}
    >
      <div className={wrapperClass}>
        <div className="iframe-modal-header">
          <div className="iframe-modal-controls">
            <div className="left-buttons-container">
              <BackButton
                theme={theme}
                label="Back"
                className="iframe-go-back-link"
                onBack={onClose}
              />
            </div>

            <div className="right-buttons-container">
              {copyLinkVisible && (
                <div className="copy-url-container">
                  <button
                    className="copy-link-btn"
                    onClick={handleCopyUrl}
                    onKeyDown={handleCopyKeyDown}
                    type="button"
                  >
                    <svg
                      className="link-simple-angled"
                      width="16"
                      height="16"
                      viewBox="0 0 35 35"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M24.6914 8.75C23.7836 8.75 22.9031 8.99609 22.132 9.44453C21.268 8.56953 20.2617 7.83672 19.1516 7.28437C20.6938 5.97187 22.657 5.25 24.6914 5.25C29.4164 5.25 33.25 9.07812 33.25 13.8086C33.25 16.0781 32.3477 18.2547 30.7453 19.857L26.857 23.7453C25.2547 25.3477 23.0781 26.25 20.8086 26.25C16.0836 26.25 12.25 22.4219 12.25 17.6914C12.25 17.6094 12.25 17.5273 12.2555 17.4453C12.2828 16.4773 13.0867 15.7172 14.0547 15.7445C15.0227 15.7719 15.7828 16.5758 15.7555 17.5437C15.7555 17.593 15.7555 17.6422 15.7555 17.6859C15.7555 20.4805 18.0195 22.7445 20.8141 22.7445C22.1539 22.7445 23.4391 22.2141 24.3906 21.2625L28.2789 17.3742C29.225 16.4281 29.7609 15.1375 29.7609 13.7977C29.7609 11.0031 27.4969 8.73906 24.7023 8.73906L24.6914 8.75ZM16.8 12.9773C16.6961 12.9336 16.5922 12.8734 16.4992 12.8078C15.8102 12.4523 15.0227 12.25 14.1969 12.25C12.857 12.25 11.5719 12.7805 10.6203 13.732L6.73203 17.6203C5.78594 18.5664 5.25 19.857 5.25 21.1969C5.25 23.9914 7.51406 26.2555 10.3086 26.2555C11.2109 26.2555 12.0914 26.0148 12.8625 25.5664C13.7266 26.4414 14.7328 27.1742 15.8484 27.7266C14.3062 29.0336 12.3484 29.7609 10.3086 29.7609C5.58359 29.7609 1.75 25.9328 1.75 21.2023C1.75 18.9328 2.65234 16.7562 4.25469 15.1539L8.14297 11.2656C9.74531 9.66328 11.9219 8.76094 14.1914 8.76094C18.9273 8.76094 22.75 12.6219 22.75 17.3414C22.75 17.4125 22.75 17.4836 22.75 17.5547C22.7281 18.5227 21.9242 19.2828 20.9563 19.2609C19.9883 19.2391 19.2281 18.4352 19.25 17.4672C19.25 17.4234 19.25 17.3852 19.25 17.3414C19.25 15.4984 18.2656 13.8797 16.8 12.9883V12.9773Z"
                        fill="#329EE2"
                      />
                    </svg>
                    <div className="copy-link-text">Copy URL</div>
                  </button>

                  {showCopied && (
                    <div className="copied-tooltip">
                      Copied!
                    </div>
                  )}
                </div>
              )}

              <div className="fullscreen-button-container">
                <button
                  className="fullscreen-toggle-btn"
                  onClick={handleFullscreenToggle}
                  onKeyDown={handleFullscreenKeyDown}
                  type="button"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                >
                  {isFullscreen ? (
                    // Exit fullscreen icon (minimize/shrink icon)
                    <svg
                      className="fullscreen-icon"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.5 4.5L12 2M12 2H9.5M12 2V4.5M4.5 9.5L2 12M2 12V9.5M2 12H4.5"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    // Enter fullscreen icon (expand icon)
                    <svg
                      className="fullscreen-icon"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 4.5L4.5 2M4.5 2V4.5M4.5 2H2M12 9.5L9.5 12M9.5 12H12M9.5 12V9.5"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                  <div className="fullscreen-button-text">
                    {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="iframe-modal-content">
          {url ? (
            <LoadingOverlay
              isLoading={isLoading}
              theme={theme}
            >
              <iframe
                src={getIframeUrl()}
                className="iframe-modal-iframe"
                title={title}
                frameBorder="0"
                allow="fullscreen"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                data-iframe-modal="true"
                onLoad={handleIframeLoad}
              />
            </LoadingOverlay>
          ) : (
            <div>Loading content...</div>
          )}
        </div>
      </div>
    </Modal>
  );
};
